/**
 * Panier v2 (SPEC § 4.7, CK1 ; retour client K1). Routes : checkout, checkout/id/:productId,
 * checkout/contract/:contractId, sca-callback, wallet-callback.
 *
 * La v2 utilise le Nouveau Panier (screens/Checkout + screens/Checkout/v2), qui n'existe que par elle :
 * le serveur injecte `checkoutConfig.enabled = UxV2::isActive()` (Frontend.php ; plus de réglage propre
 * depuis le 09/10/2026). Apple Pay / Google Pay restent pilotés par leurs propres réglages.
 *
 * AUCUNE logique de paiement ici : les écrans v1 sont rendus tels quels (useCheckoutForm, SCA/3DS,
 * Alma, portefeuilles et leur reprise, GA4, Sentry), dans une enveloppe `.mmv2-checkout` que
 * checkout.css ré-habille aux jetons v2 et à la maquette /msc (`checkout()` : étapes, lignes de
 * paiement, Alma, promo, carte cadeau, récapitulatif collant).
 *
 * Une seule option de mise en page (screens/Checkout/v2/layout.ts, sans fournisseur = rendu historique) : le bloc
 * Avantages du Nouveau Panier est rendu dans l'accordéon « Code promo / Carte cadeau » (AdvantagesAccordion),
 * au-dessus de « Moyen de paiement » (retour de Luc du 08/10).
 */
import { VisuallyHidden } from '@chakra-ui/react';
import { useEffect, useRef, useState, type MouseEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Checkout } from 'screens/Checkout';
import { ScaCallback } from 'screens/Checkout/ScaCallback';
import { CheckoutLayoutContext, type CheckoutLayout } from 'screens/Checkout/v2/layout';
import { WalletCallback } from 'screens/Checkout/WalletCallback';
import { injectV2Css } from '../../styles/inject';
import { AdvantagesAccordion } from './AdvantagesAccordion';
import checkoutCss from './checkout.css?inline';

injectV2Css('screens/Checkout/checkout.css', checkoutCss);

/** Mise en page v2 du Nouveau Panier : avantages en accordéon, au-dessus du moyen de paiement. */
const CHECKOUT_LAYOUT_V2: CheckoutLayout = { AdvantagesFrame: AdvantagesAccordion };

/**
 * Seul h1 de la route (un h1 par écran v2) : le titre visible du Nouveau Panier (« Paiement », puis « Merci »)
 * est un h2 du v1 (.cv2-page-title, Stepper.tsx), que la v2 ne modifie pas. Masqué visuellement. Suit l'étape : la
 * confirmation (étape 3, .CheckoutV2--confirmed, rendue sur la même route) a son propre titre (revue du 04/10 : le h1
 * restait « Paiement » sur l'écran « Merci »).
 */
function PageTitle() {
  const ref = useRef<HTMLHeadingElement>(null);
  const [confirmed, setConfirmed] = useState(false);
  useEffect(() => {
    const root = ref.current?.closest('.mmv2-checkout');
    if (!root || typeof MutationObserver === 'undefined') return undefined;
    const check = () => setConfirmed(!!root.querySelector('.CheckoutV2--confirmed'));
    check();
    const observer = new MutationObserver(check);
    observer.observe(root, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);
  return (
    <VisuallyHidden as="h1" className="mmv2-checkout-h1" ref={ref}>
      {confirmed ? 'Confirmation de l’achat' : 'Paiement'}
    </VisuallyHidden>
  );
}

export function CheckoutV2() {
  const navigate = useNavigate();
  const { pathname, state } = useLocation();
  /*
   * Panier d'un abonnement : la pastille « Offre » du fil des étapes (Stepper v1, navigate('/tarifs')) ramène sur le
   * segment ABONNEMENTS des Tarifs, pas sur PACKS (revue du 05/10). Interception à la capture, v2 seulement : le
   * composant v1 n'est pas modifié. Venu de la page Tarifs : retour arrière, comme « Retour » (segment, sous-onglet
   * et défilement restitués) ; sinon le sous-onglet qui contient l'abonnement, page amenée sur les offres.
   */
  const onClickCapture = (event: MouseEvent<HTMLDivElement>) => {
    if (!pathname.startsWith('/checkout/contract/')) return;
    if (!(event.target as HTMLElement).closest?.('.cv2-step-btn')) return;
    event.preventDefault();
    event.stopPropagation();
    const from = (state as { from?: { pathname?: string } } | null)?.from;
    if (from?.pathname === '/tarifs') {
      navigate(-1);
      return;
    }
    const contractId = pathname.split('/')[3] ?? '';
    const subTabs =
      (typeof mondayMindbody !== 'undefined' && mondayMindbody?.pricingConfig?.segments?.abonnements?.subTabs) || [];
    const tab = subTabs.find((t) => (t.contractIds ?? []).map(String).includes(contractId));
    const query = new URLSearchParams({ segment: 'abonnements', ...(tab ? { onglet: String(tab.id) } : {}) });
    navigate(`/tarifs?${query.toString()}`, { state: { scrollToOffers: true } });
  };
  return (
    <div className="mmv2-checkout" data-screen="checkout" onClickCapture={onClickCapture}>
      <PageTitle />
      <CheckoutLayoutContext.Provider value={CHECKOUT_LAYOUT_V2}>
        <Checkout />
      </CheckoutLayoutContext.Provider>
    </div>
  );
}

/** Retour 3-D Secure : écran v1 (finalisation de l'achat après authentification) dans l'enveloppe v2. */
export function ScaCallbackV2() {
  return (
    <div className="mmv2-checkout mmv2-checkout--callback" data-screen="sca-callback">
      <PageTitle />
      <ScaCallback />
    </div>
  );
}

/** Retour de la page de paiement Apple Pay / Google Pay : écran v1 (fenêtre de suivi) tel quel. */
export function WalletCallbackV2() {
  return (
    <div className="mmv2-checkout mmv2-checkout--callback" data-screen="wallet-callback">
      <PageTitle />
      <WalletCallback />
    </div>
  );
}
